/**
 * Mixin para exportación optimizada a Excel con procesamiento asíncrono
 * Evita bloqueos del navegador al exportar grandes volúmenes de datos
 */

import { AsyncDataProcessor } from '@/utils/asyncDataProcessor';
import XLSXpopulate from 'xlsx-populate';

export default {
  data() {
    return {
      exportProgress: 0,
      isExporting: false,
      exportAbortController: null
    };
  },

  methods: {
    /**
     * Genera el archivo Excel de forma asíncrona y optimizada
     */
    async generateExcelFileAsync(data, template) {
      // Mostrar progreso inicial
      this.$swal.fire({
        title: "Generando reporte Excel...",
        html: `
          <div class="text-center">
            <div class="mb-3">
              <i class="fas fa-file-excel fa-3x text-success mb-3"></i>
            </div>
            <div class="progress mb-3" style="height: 25px;">
              <div id="export-progress-bar" class="progress-bar progress-bar-striped progress-bar-animated bg-success" 
                   role="progressbar" style="width: 0%">
                0%
              </div>
            </div>
            <p class="text-muted small">
              <span id="export-status">Iniciando exportación...</span>
            </p>
            <p class="text-muted small">
              <i class="fas fa-info-circle"></i> 
              Procesando <strong>${data.length.toLocaleString()}</strong> registros
            </p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        }
      });

      try {
        // Crear controlador de aborto
        this.exportAbortController = new AbortController();

        const wb = await XLSXpopulate.fromDataAsync(template);
        
        // Obtener todas las hojas
        const sheets = {
          consolidado: wb.sheet(0),
          ventas: wb.sheet(1),
          control: wb.sheet(2),
          productos: wb.sheet(3),
          membresias: wb.sheet(4),
          dte: wb.sheet(5)
        };

        // Configurar fecha de generación en todas las hojas
        const fechaGeneracion = this.$moment().format("DD/MM/YYYY - HH:mm:ss");
        Object.values(sheets).forEach(sheet => {
          if (sheet.cell("D3")) {
            sheet.cell("D3").value(fechaGeneracion);
          }
        });

        // Contadores de filas
        const rowCounters = {
          consolidado: 7,
          ventas: 7,
          control: 7,
          productos: 7,
          membresias: 7,
          dte: 7
        };

        // Procesar datos en chunks
        await AsyncDataProcessor.processInChunks(
          data,
          async (chunk, startIndex, endIndex) => {
            // Verificar si se abortó la exportación
            if (this.exportAbortController.signal.aborted) {
              throw new Error('Exportación cancelada por el usuario');
            }

            for (const record of chunk) {
              // HOJA 1: CONSOLIDADO
              await this.fillSheetRowAsync(
                sheets.consolidado,
                record,
                rowCounters.consolidado++,
                'fillConsolidadoSheet'
              );

              // HOJA 2: VENTAS
              await this.fillSheetRowAsync(
                sheets.ventas,
                record,
                rowCounters.ventas++,
                'fillVentasSheet'
              );

              // HOJA 3: CONTROL Y DETALLE (múltiples filas por formas de pago)
              const formasPago = record.infoVenta?.venta || [];
              if (formasPago.length > 0) {
                for (const formaPago of formasPago) {
                  await this.fillSheetRowAsync(
                    sheets.control,
                    record,
                    rowCounters.control++,
                    'fillControlSheet',
                    formaPago
                  );
                }
              } else {
                await this.fillSheetRowAsync(
                  sheets.control,
                  record,
                  rowCounters.control++,
                  'fillControlSheet',
                  {}
                );
              }

              // HOJA 4: PRODUCTOS (múltiples filas por producto)
              const productos = record.infoVenta?.detalleVenta || [];
              if (productos.length > 0) {
                for (const producto of productos) {
                  await this.fillSheetRowAsync(
                    sheets.productos,
                    record,
                    rowCounters.productos++,
                    'fillProductosSheet',
                    producto
                  );
                }
              } else {
                await this.fillSheetRowAsync(
                  sheets.productos,
                  record,
                  rowCounters.productos++,
                  'fillProductosSheet',
                  {}
                );
              }

              // HOJA 5: MEMBRESIAS (múltiples filas por plan)
              const planes = record.infoVenta?.plan || [];
              if (planes.length > 0) {
                for (const plan of planes) {
                  await this.fillSheetRowAsync(
                    sheets.membresias,
                    record,
                    rowCounters.membresias++,
                    'fillMembresiasSheet',
                    plan
                  );
                }
              } else {
                await this.fillSheetRowAsync(
                  sheets.membresias,
                  record,
                  rowCounters.membresias++,
                  'fillMembresiasSheet',
                  {}
                );
              }

              // HOJA 6: DTE (múltiples filas por documento)
              if (record.dte?.bsale) {
                const dte = record.dte.bsale;
                
                // Procesar cada tipo de documento
                const dteTypes = [
                  { type: 'FACTURA', data: dte.facturaElectronica },
                  { type: 'BOLETA', data: dte.boletaElectronica },
                  { type: 'NOTA_CREDITO', data: dte.notaCredito }
                ];

                for (const dteType of dteTypes) {
                  if (dteType.data && Object.keys(dteType.data).length > 0) {
                    await this.fillSheetRowAsync(
                      sheets.dte,
                      record,
                      rowCounters.dte++,
                      'fillDTESheet',
                      dteType.type,
                      dteType.data
                    );
                  }
                }
              }
            }

            // Actualizar progreso
            const progress = Math.round((endIndex / data.length) * 100);
            this.updateExportProgress(progress, `Procesando registros ${startIndex + 1} a ${endIndex} de ${data.length}`);
          },
          {
            chunkSize: 50, // Procesar 50 registros a la vez
            delay: 10, // 10ms de delay entre chunks
            onProgress: (progressInfo) => {
              this.exportProgress = progressInfo.percentage;
            }
          }
        );

        // Generar el archivo final
        this.updateExportProgress(95, 'Generando archivo Excel...');
        
        const blob = await wb.outputAsync();
        
        this.updateExportProgress(100, 'Descargando archivo...');
        
        // Descargar el archivo
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(
            blob,
            `Registro Ventas Gimnasio - ${this.$moment().format("DD-MM-yyyy - HH-mm-ss")}.xlsx`
          );
        } else {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          document.body.appendChild(a);
          a.href = url;
          a.download = `Registro Ventas Gimnasio - ${this.$moment().format("DD-MM-yyyy - HH-mm-ss")}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }

        // Mostrar éxito
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación Exitosa!',
          html: `
            <div class="text-center">
              <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
              <p>Se exportaron exitosamente:</p>
              <ul class="list-unstyled">
                <li><strong>${data.length.toLocaleString()}</strong> registros de ventas</li>
                <li><strong>${rowCounters.consolidado - 7}</strong> filas en hoja Consolidado</li>
                <li><strong>${rowCounters.control - 7}</strong> formas de pago detalladas</li>
                <li><strong>${rowCounters.productos - 7}</strong> productos vendidos</li>
                <li><strong>${rowCounters.membresias - 7}</strong> membresías</li>
                <li><strong>${rowCounters.dte - 7}</strong> documentos tributarios</li>
              </ul>
            </div>
          `,
          confirmButtonColor: '#6bd098',
          timer: 5000,
          timerProgressBar: true
        });

      } catch (error) {
        console.error('Error en exportación:', error);
        
        if (error.message === 'Exportación cancelada por el usuario') {
          this.$swal.fire({
            icon: 'info',
            title: 'Exportación Cancelada',
            text: 'La exportación fue cancelada por el usuario',
            confirmButtonColor: '#6bd098'
          });
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Error al Exportar',
            html: `
              <div class="text-left">
                <p><strong>Error:</strong> ${error.message || 'Error desconocido'}</p>
                <p class="text-muted small mt-2">
                  Si el problema persiste con grandes volúmenes de datos, 
                  intente exportar rangos más pequeños de fechas.
                </p>
              </div>
            `,
            confirmButtonColor: '#6bd098',
            showCancelButton: true,
            cancelButtonText: 'Cerrar',
            confirmButtonText: 'Reintentar',
            reverseButtons: true
          }).then((result) => {
            if (result.isConfirmed) {
              // Reintentar exportación
              this.exportRecords();
            }
          });
        }
      } finally {
        this.isExporting = false;
        this.exportProgress = 0;
        this.exportAbortController = null;
      }
    },

    /**
     * Llena una fila de hoja de forma asíncrona
     */
    async fillSheetRowAsync(sheet, record, row, methodName, ...extraParams) {
      // Usar el método original pero de forma asíncrona
      return new Promise((resolve) => {
        // Ejecutar en el próximo tick para no bloquear
        setTimeout(() => {
          if (this[methodName]) {
            this[methodName](sheet, record, ...extraParams, row);
          }
          resolve();
        }, 0);
      });
    },

    /**
     * Actualiza el progreso de exportación en el modal
     */
    updateExportProgress(percentage, status) {
      const progressBar = document.getElementById('export-progress-bar');
      const statusText = document.getElementById('export-status');
      
      if (progressBar) {
        progressBar.style.width = `${percentage}%`;
        progressBar.textContent = `${percentage}%`;
        progressBar.setAttribute('aria-valuenow', percentage);
      }
      
      if (statusText && status) {
        statusText.textContent = status;
      }
    },

    /**
     * Descarga los datos para exportación de forma optimizada
     */
    async createGymSalesRecordsExportDataAsync(filters) {
      console.log('📊 Preparando parámetros para exportación optimizada:', filters);
      
      // Convertir filtros del componente a parámetros de API
      let Parameters = {};
      
      // [Mantener el mapeo de parámetros existente...]
      if (filters.status) {
        if (filters.status === '[VIGENTE]') {
          Parameters.estadoVenta = "";
        } else if (filters.status === '[ANULADA]') {
          Parameters.estadoVenta = "[ANULADA]";
        } else {
          Parameters.estadoVenta = filters.status;
        }
      }
      
      if (filters.paymentMethod) {
        Parameters.metodoPago = filters.paymentMethod;
      }
      
      if (filters.branch) {
        Parameters.nombreSucursal = filters.branch;
      }
      
      if (filters.dateFrom) {
        Parameters.fechaDesde = filters.dateFrom;
      }
      
      if (filters.dateTo) {
        Parameters.fechaHasta = filters.dateTo;
      }
      
      if (filters.searchText && filters.searchText.trim()) {
        const searchText = filters.searchText.trim();
        if (!isNaN(searchText) && searchText.length > 0) {
          Parameters.idTransaccion = searchText;
        } else if (searchText.includes('-') && searchText.length >= 9) {
          Parameters.rutCliente = searchText;
        } else {
          Parameters.nombreCliente = searchText;
        }
      }
      
      let keepFetching = true;
      let page = 0;
      let data = [];
      const maxConcurrentRequests = 3; // Máximo de peticiones concurrentes

      this.$swal.fire({
        title: "Descargando registros de ventas...",
        html: `
          <div class="text-center">
            <div class="mb-3">
              <i class="fas fa-download fa-3x text-primary mb-3"></i>
            </div>
            <div class="progress mb-3" style="height: 25px;">
              <div id="download-progress-bar" class="progress-bar progress-bar-striped progress-bar-animated" 
                   role="progressbar" style="width: 0%">
                0 registros
              </div>
            </div>
            <p class="text-muted small">
              <span id="download-status">Iniciando descarga...</span>
            </p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        }
      });

      try {
        // Implementar descarga con peticiones concurrentes limitadas
        const downloadBatch = async (startPage, batchSize) => {
          const promises = [];
          
          for (let i = 0; i < batchSize; i++) {
            const currentPage = startPage + i;
            
            const promise = this.$CentralGymOPS_API.post(
              "",
              {
                Header: {
                  Operacion: "/v1/sales/get/gymsales/reportformat"
                },
                Auth: {
                  Token: sessionStorage.getItem("jCxbraoQeD")
                },
                Parameters: { ...Parameters, page: currentPage },
                Body: {}
              },
              {
                headers: {
                  "Content-Type": "application/json"
                }
              }
            ).then(response => {
              if (response.data && response.data.result && response.data.result.length > 0) {
                return {
                  page: currentPage,
                  data: response.data.result
                };
              }
              return null;
            }).catch(error => {
              console.error(`Error en página ${currentPage}:`, error);
              return null;
            });
            
            promises.push(promise);
          }
          
          return Promise.all(promises);
        };

        // Descargar en lotes concurrentes
        while (keepFetching) {
          const batchResults = await downloadBatch(page, maxConcurrentRequests);
          
          let hasMoreData = false;
          for (const result of batchResults) {
            if (result && result.data.length > 0) {
              data.push(...result.data);
              hasMoreData = result.data.length === 100;
              
              // Actualizar progreso
              const progressBar = document.getElementById('download-progress-bar');
              const statusText = document.getElementById('download-status');
              
              if (progressBar) {
                progressBar.textContent = `${data.length.toLocaleString()} registros`;
                progressBar.style.width = Math.min((data.length / 1000) * 100, 90) + '%';
              }
              
              if (statusText) {
                statusText.textContent = `Descargando página ${result.page + 1}...`;
              }
            }
          }
          
          page += maxConcurrentRequests;
          keepFetching = hasMoreData;
        }

        console.log('✅ Descarga completada. Total de registros:', data.length);
        return data;
        
      } catch (error) {
        console.error('❌ Error en descarga:', error);
        throw error;
      }
    }
  }
};